import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Table,
  Modal,
  message,
  Space,
  Typography,
  Divider,
  Card,
  Row,
  Col,
  Tag,
  Progress,
  Alert,
  Tooltip,
  Popconfirm,
  Descriptions,
  Statistic,
  Select,
} from 'antd';

import {
  UploadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  StopOutlined,
  HistoryOutlined,
} from '@ant-design/icons';

// 使用命名导入
import { excelUploadService } from '../services/excelUploadService';
import { authService } from '../services/authService';
import { companyService } from '../services/companyService';
import { useI18n } from '../hooks/useI18n';

import './JobManager.css';
import './common.css';

const { Title, Text } = Typography;

// 添加 API_BASE_URL 常量
const API_BASE_URL = '/api';

// 定义Job类型
interface JobInfo {
  id: string;
  status: 'idle' | 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  timestamp: string;
  estimatedTime?: string;
  fileName?: string;
  fileSize?: number;
  tenantCode?: string;
}

// 定义组件Props
interface JobManagerProps {
  mode?: 'models' | 'instructions' | 'sql-pairs' | 'both';
}

const JobManager: React.FC<JobManagerProps> = ({ mode = 'both' }) => {
  const { t } = useI18n();
  
  // 状态管理
  const [jobs, setJobs] = useState<JobInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncModalType, setSyncModalType] = useState<'models' | 'instructions' | 'sql-pairs'>('models');
  const [jobDetailsModalVisible, setJobDetailsModalVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobInfo | null>(null);
  const [selectedJobDetails, setSelectedJobDetails] = useState<any>(null);
  const [selectedJobLogs, setSelectedJobLogs] = useState<any[]>([]);
  const [loadingJobDetails, setLoadingJobDetails] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [currentJobProgress, setCurrentJobProgress] = useState<any>(null);
  const [currentJobStatus, setCurrentJobStatus] = useState<string>('idle');
  const [currentJobEvents, setCurrentJobEvents] = useState<any[]>([]);
  const [modelingType, setModelingType] = useState<'SQLSERVER' | 'HANA'>('SQLSERVER');
  /** Excel 发布模型：目标 project_id（与公司管理一致，与 Modeling 导入相同数据源） */
  const [publishProjectOptions, setPublishProjectOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [publishProjectId, setPublishProjectId] = useState<string>('');
  const [publishProjectOptionsLoading, setPublishProjectOptionsLoading] = useState(false);

  // 修复 useRef 的类型和初始值
  const currentJobProgressRef = useRef<EventSource | null>(null);
  const currentJobIdRef = useRef<string>(''); // 添加单独的 jobId 引用

  // 创建带认证的 fetch 函数
  const createAuthenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('token');
    const currentTenant = localStorage.getItem('currentTenant');
    
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'X-Tenant-Code': currentTenant || 'default'
    };

    return fetch(url, {
      ...options,
      headers
    });
  };

  const resolveSyncApiPrefix = (
    target: 'models' | 'instructions' | 'sql-pairs' = mode === 'instructions'
      ? 'instructions'
      : mode === 'sql-pairs'
      ? 'sql-pairs'
      : 'models'
  ): string => {
    if (target === 'instructions') return 'sync_instruction';
    if (target === 'sql-pairs') return 'sync_sql_pair';
    return 'sync_model';
  };

  const startProgressMonitoringForType = (
    jobId: string,
    target: 'models' | 'instructions' | 'sql-pairs'
  ) => {
    if (target === 'instructions') {
      startInstructionSyncProgressMonitoring(jobId);
    } else if (target === 'sql-pairs') {
      startSqlPairSyncProgressMonitoring(jobId);
    } else {
      startModelSyncProgressMonitoring(jobId);
    }
  };

  const isTerminalSyncEvent = (data: any) =>
    data.type === 'completed' ||
    data.type === 'failed' ||
    data.status === 'completed' ||
    data.status === 'failed' ||
    data.data?.status === 'completed' ||
    data.data?.status === 'failed';

  const isSuccessfulSyncEvent = (data: any) =>
    data.type === 'completed' ||
    data.status === 'completed' ||
    data.data?.status === 'completed';

  // 打开发布模型 / 发布指令弹窗时加载公司列表，用于选择 project_id
  useEffect(() => {
    if (!syncModalVisible || (syncModalType !== 'models' && syncModalType !== 'instructions' && syncModalType !== 'sql-pairs')) return;
    let cancelled = false;
    setPublishProjectOptionsLoading(true);
    (async () => {
      try {
        const tenant = authService.getCurrentTenant() || 'default';
        const companyResp = await companyService.getCompanies(tenant);
        const companies = companyResp.data || [];
        const opts = companies
          .filter((c: any) => c?.project_id != null && String(c.project_id).trim() !== '')
          .map((c: any) => ({
            value: String(c.project_id),
            label: `${c.company_name || c.project_id} (${c.project_id})`.trim()
          }));
        if (cancelled) return;
        setPublishProjectOptions(opts);
        setPublishProjectId((prev) => {
          if (prev && opts.some((o) => o.value === prev)) return prev;
          return opts[0]?.value ?? '';
        });
      } catch {
        if (!cancelled) {
          setPublishProjectOptions([]);
          setPublishProjectId('');
        }
      } finally {
        if (!cancelled) setPublishProjectOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [syncModalVisible, syncModalType]);

  // 加载任务列表
  const loadJobs = async () => {
    try {
      setLoading(true);
      const currentTenant = authService.getCurrentTenant() || 'default';
      const jobsPath =
        mode === 'instructions'
          ? 'sync_instruction/jobs'
          : mode === 'sql-pairs'
          ? 'sync_sql_pair/jobs'
          : 'sync_model/jobs';
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/${jobsPath}`);
      const result = await response.json();
      
      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : result.data?.jobs ?? [];
        setJobs(
          list.map((job: any) => ({
            ...job,
            fileName: job.fileName || job.request?.excel_file_path?.split('/').pop(),
            timestamp: job.timestamp || job.created_at,
            tenantCode: job.tenantCode || job.tenant_code,
          }))
        );
      } else {
        message.error(t('jobManager.loadJobsError'));
      }
    } catch (error) {
      console.error('加载任务列表失败:', error);
      message.error(t('jobManager.loadJobsError'));
    } finally {
      setLoading(false);
    }
  };

  // 获取任务统计
  const loadJobStats = async () => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const statsPath = `${resolveSyncApiPrefix()}/jobs/stats`;
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/${statsPath}`);
      const result = await response.json();
      
      if (result.success) {
        // 更新统计信息
        // setJobStats(result.data); // This state variable doesn't exist in the original file
      }
    } catch (error) {
      console.error('加载任务统计失败:', error);
    }
  };

  // 处理模型同步Excel上传
  const handleModelSyncUpload = async (file: File) => {
    try {
      console.log('开始处理文件上传:', file.name, file.size);
              setCurrentJobStatus('pending');

      if (!publishProjectId) {
        message.warning(t('modeling.importProjectRequired'));
        setCurrentJobStatus('idle');
        return;
      }
      
      // 验证文件类型
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        message.error(t('jobManager.fileTypeError'));
        setCurrentJobStatus('idle');
        return;
      }

      console.log('文件验证通过，开始调用excelUploadService');

      // 获取当前租户
      const currentTenant = authService.getCurrentTenant() || 'default';
      
      // 调用excelUploadService进行模型同步
      const result = await excelUploadService.uploadAndSync(file, currentTenant, modelingType as any, publishProjectId);
      
      console.log('excelUploadService返回结果:', result);
      
      if (result.success) {
        setCurrentJobId(result.data.jobId);
        setCurrentJobStatus('processing');
        setCurrentJobEvents([]);
        
        // 开始监听进度
        console.log('开始监听进度，JobId:', result.data.jobId);
        startModelSyncProgressMonitoring(result.data.jobId);
        
        message.success(t('jobManager.publishStarted'));
        
        // 添加到任务列表
        const newJob: JobInfo = {
          id: result.data.jobId,
          status: 'processing',
          progress: 0,
          message: t('jobManager.info.publishInProgress'),
          timestamp: new Date().toISOString(),
          fileName: file.name,
          fileSize: file.size,
          tenantCode: 'default'
        };
        setJobs(prev => [newJob, ...prev]);
        
      } else {
        throw new Error(result.message || t('jobManager.publishStartError'));
      }
      
    } catch (error: any) {
      console.error('文件上传处理失败:', error);
      message.error(`${t('jobManager.publishFailed')}: ${error.message}`);
      setCurrentJobStatus('failed');
    }
  };

  /**
   * 开始模型同步进度监控
   */
  const startModelSyncProgressMonitoring = (jobId: string) => {
    // 安全地关闭现有的连接
    if (currentJobProgressRef.current) {
      try {
        currentJobProgressRef.current.close();
        currentJobProgressRef.current = null;
      } catch (error) {
        console.error('关闭现有SSE连接失败:', error);
      }
    }

    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const eventSource = new EventSource(
        `${API_BASE_URL}/${currentTenant}/sync_model/progress/${jobId}`
      );

      // 保存当前的 jobId
      currentJobIdRef.current = jobId;

      // 设置连接建立事件
      eventSource.onopen = () => {
        console.log('SSE连接已建立');
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('收到SSE消息:', data);

          // 只处理非心跳消息
          if (data.type === 'heartbeat') {
            console.log('收到心跳消息，跳过处理');
            return;
          }

          // 收集所有事件到事件列表
          setCurrentJobEvents(prev => [...prev, data]);

          // 更新当前任务进度
          if (data.type === 'progress' || data.type === 'status') {
            setCurrentJobProgress({
              jobId,
              status: data.status,
              progress: data.progress || 0,
              message: data.message || '',
              currentStep: data.currentStep || 0,
              totalSteps: data.totalSteps || 0,
              currentModel: data.currentModel || '',
              processedModels: data.processedModels || 0,
              totalModels: data.totalModels || 0,
              error: data.error,
              errors: data.errors
            });

            // 同时更新任务列表中的进度
            setJobs(prevJobs => 
              prevJobs.map(job => 
                job.id === jobId 
                  ? { 
                      ...job, 
                      status: data.status,
                      progress: data.progress || 0,
                      message: data.message || '',
                      currentStep: data.currentStep || 0,
                      totalSteps: data.totalSteps || 0,
                      currentModel: data.currentModel || '',
                      processedModels: data.processedModels || 0,
                      totalModels: data.totalModels || 0,
                      updated_at: new Date().toISOString()
                    }
                  : job
              )
            );
          }

          // 如果任务完成，关闭连接
          if (data.status === 'completed' || data.status === 'failed') {
            console.log('任务完成，关闭SSE连接');
            closeEventSource();
            
            // 显示完成消息
            if (data.status === 'completed') {
              message.success(t('jobManager.publishCompleted'));
            } else {
              message.error(t('jobManager.publishError'));
            }
          }

        } catch (error) {
          console.error('解析SSE消息失败:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE连接错误:', error);
        closeEventSource();
      };

      // 保存引用
      currentJobProgressRef.current = eventSource;
      console.log('SSE连接已建立，任务ID:', jobId);

    } catch (error) {
      console.error('建立SSE连接失败:', error);
      message.error(t('jobManager.establishConnectionError'));
    }
  };

  /**
   * 开始Instructions同步进度监控
   */
  const startInstructionSyncProgressMonitoring = (jobId: string) => {
    // 安全地关闭现有的连接
    if (currentJobProgressRef.current) {
      try {
        currentJobProgressRef.current.close();
        currentJobProgressRef.current = null;
      } catch (error) {
        console.error('关闭现有SSE连接失败:', error);
      }
    }

    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const eventSource = new EventSource(
        `${API_BASE_URL}/${currentTenant}/sync_instruction/progress/${jobId}`
      );

      // 保存当前的 jobId
      currentJobIdRef.current = jobId;

      // 设置连接建立事件
      eventSource.onopen = () => {
        console.log('SSE连接已建立');
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('收到SSE消息:', data);

          // 只处理非心跳消息
          if (data.type === 'heartbeat') {
            console.log('收到心跳消息，跳过处理');
            return;
          }

          // 收集所有事件到事件列表（除了心跳和连接消息）
          if (data.type !== 'connected' && data.type !== 'heartbeat') {
            setCurrentJobEvents(prev => [...prev, {
              ...data,
              timestamp: data.timestamp || data.data?.timestamp || new Date().toISOString(),
              message: data.message || data.data?.message || ''
            }]);
          }

          // 更新当前任务进度
          if (data.type === 'progress' || data.type === 'status') {
            const rawProgressData = data.data || data;
            // 根据任务类型设置字段（Instruction 或 Model）
            const progressData: any = {
              jobId,
              status: rawProgressData.status || data.status,
              progress: rawProgressData.progress || data.progress || 0,
              message: rawProgressData.message || data.message || '',
              currentStep: rawProgressData.currentStep || data.currentStep || 0,
              totalSteps: rawProgressData.totalSteps || data.totalSteps || 0,
              updated_at: new Date().toISOString()
            };
            
            // Instruction 同步特定字段
            if (rawProgressData.currentInstruction !== undefined || rawProgressData.processedInstructions !== undefined || rawProgressData.totalInstructions !== undefined) {
              progressData.currentInstruction = rawProgressData.currentInstruction || '';
              progressData.processedInstructions = rawProgressData.processedInstructions || 0;
              progressData.totalInstructions = rawProgressData.totalInstructions || 0;
              progressData.isInstructionSync = true;
            }
            
            // Model 同步特定字段
            if (rawProgressData.currentModel !== undefined || rawProgressData.processedModels !== undefined || rawProgressData.totalModels !== undefined) {
              progressData.currentModel = rawProgressData.currentModel || '';
              progressData.processedModels = rawProgressData.processedModels || 0;
              progressData.totalModels = rawProgressData.totalModels || 0;
              progressData.isInstructionSync = false;
            }
            
            setCurrentJobProgress(progressData);

            // 同时更新任务列表中的进度
            setJobs(prevJobs => 
              prevJobs.map(job => 
                job.id === jobId 
                  ? { 
                      ...job, 
                      ...progressData
                    }
                  : job
              )
            );
          }

          if (isTerminalSyncEvent(data)) {
            console.log('任务完成，关闭SSE连接');
            closeEventSource();
            if (isSuccessfulSyncEvent(data)) {
              message.success(t('jobManager.instructionsPublishCompleted'));
            } else {
              message.error(t('jobManager.instructionsPublishError'));
            }
          }

        } catch (error) {
          console.error('解析SSE消息失败:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE连接错误:', error);
        closeEventSource();
      };

      // 保存引用
      currentJobProgressRef.current = eventSource;
      console.log('SSE连接已建立，任务ID:', jobId);

    } catch (error) {
      console.error('建立SSE连接失败:', error);
      message.error('建立进度监控连接失败');
    }
  };

  const startSqlPairSyncProgressMonitoring = (jobId: string) => {
    if (currentJobProgressRef.current) {
      try {
        currentJobProgressRef.current.close();
        currentJobProgressRef.current = null;
      } catch (error) {
        console.error('关闭现有SSE连接失败:', error);
      }
    }

    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const eventSource = new EventSource(
        `${API_BASE_URL}/${currentTenant}/sync_sql_pair/progress/${jobId}`
      );
      currentJobIdRef.current = jobId;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'heartbeat' || data.type === 'connected') return;

          if (data.type !== 'connected' && data.type !== 'heartbeat') {
            setCurrentJobEvents((prev) => [
              ...prev,
              {
                ...data,
                timestamp: data.timestamp || data.data?.timestamp || new Date().toISOString(),
                message: data.message || data.data?.message || '',
              },
            ]);
          }

          if (data.type === 'progress' || data.type === 'status') {
            const raw = data.data || data;
            const progressData: any = {
              jobId,
              status: raw.status || data.status,
              progress: raw.progress || data.progress || 0,
              message: raw.message || data.message || '',
              currentStep: raw.currentStep || data.currentStep || 0,
              totalSteps: raw.totalSteps || data.totalSteps || 0,
              updated_at: new Date().toISOString(),
              isSqlPairSync: true,
              currentSqlPair: raw.currentSqlPair || '',
              processedSqlPairs: raw.processedSqlPairs || 0,
              totalSqlPairs: raw.totalSqlPairs || 0,
            };
            setCurrentJobProgress(progressData);
            setJobs((prevJobs) =>
              prevJobs.map((job) => (job.id === jobId ? { ...job, ...progressData } : job))
            );
          }

          if (isTerminalSyncEvent(data)) {
            closeEventSource();
            if (isSuccessfulSyncEvent(data)) {
              message.success(t('jobManager.sqlPairsPublishCompleted'));
            } else {
              message.error(t('jobManager.sqlPairsPublishError'));
            }
          }
        } catch (error) {
          console.error('解析SSE消息失败:', error);
        }
      };

      eventSource.onerror = () => closeEventSource();
      currentJobProgressRef.current = eventSource;
    } catch (error) {
      console.error('建立SSE连接失败:', error);
      message.error(t('jobManager.establishConnectionError'));
    }
  };

  /**
   * 安全地关闭 EventSource 连接
   */
  const closeEventSource = () => {
    if (currentJobProgressRef.current) {
      try {
        currentJobProgressRef.current.close();
        currentJobProgressRef.current = null;
        currentJobIdRef.current = ''; // 清空 jobId
        console.log('SSE连接已关闭');
      } catch (error) {
        console.error('关闭SSE连接失败:', error);
      }
    }
  };

  /**
   * 重置当前任务进度
   */
  const resetCurrentJob = () => {
    setCurrentJobProgress({
      jobId: '',
      status: '',
      progress: 0,
      message: '',
      currentStep: 0,
      totalSteps: 0,
      currentModel: '',
      processedModels: 0,
      totalModels: 0
    });
    
    // 关闭SSE连接
    closeEventSource();
  };

  /**
   * 手动刷新进度
   */
  const refreshProgress = async (jobId: string) => {
    try {
      console.log(`手动刷新进度，JobId: ${jobId}`);
      console.log(`当前监控的任务ID: ${currentJobIdRef.current}`);

      const currentTenant = authService.getCurrentTenant() || 'default';
      const monitorType: 'models' | 'instructions' | 'sql-pairs' =
        mode === 'instructions' ? 'instructions' : mode === 'sql-pairs' ? 'sql-pairs' : 'models';

      let jobData: any = null;
      if (monitorType === 'models') {
        const response = await excelUploadService.getSyncStatus(currentTenant, jobId);
        jobData = response?.data;
      } else {
        const response = await createAuthenticatedFetch(
          `${API_BASE_URL}/${currentTenant}/${resolveSyncApiPrefix(monitorType)}/jobs/${jobId}`
        );
        const result = await response.json();
        jobData = result?.data;
      }

      if (jobData) {
        console.log('获取到的任务数据:', jobData);

        setJobs(prevJobs =>
          prevJobs.map(job =>
            job.id === jobId
              ? {
                  ...job,
                  status: jobData.status,
                  progress: jobData.progress || 0,
                  message: jobData.currentMessage || jobData.message || '',
                  updated_at: jobData.updated_at || new Date().toISOString(),
                }
              : job
          )
        );

        if (currentJobIdRef.current === jobId) {
          console.log('检测到当前监控任务，准备重新建立SSE连接');
          closeEventSource();
          setTimeout(() => {
            console.log('开始重新建立SSE连接...');
            startProgressMonitoringForType(jobId, monitorType);
          }, 100);
          message.success(t('jobManager.refreshWithReconnect'));
        } else {
          console.log('非当前监控任务，仅更新状态');
          message.success(t('jobManager.refreshSuccess'));
        }
      } else {
        message.error(t('jobManager.getTaskStatusError'));
      }
    } catch (error: any) {
      console.error('手动刷新进度失败:', error);
      message.error(`${t('jobManager.refreshError')}: ${error.message}`);
    }
  };

  /**
   * 停止任务
   */
  const stopJob = async (jobId: string) => {
    try {
      console.log(`开始停止任务: ${jobId}`);
      message.loading(t('jobManager.stoppingTask'), 0);

      const currentTenant = authService.getCurrentTenant() || 'default';
      const monitorType: 'models' | 'instructions' | 'sql-pairs' =
        mode === 'instructions' ? 'instructions' : mode === 'sql-pairs' ? 'sql-pairs' : 'models';
      let response: any;
      if (monitorType === 'models') {
        response = await excelUploadService.stopJob(currentTenant, jobId);
      } else {
        const fetchResponse = await createAuthenticatedFetch(
          `${API_BASE_URL}/${currentTenant}/${resolveSyncApiPrefix(monitorType)}/jobs/${jobId}/stop`,
          { method: 'POST' }
        );
        response = await fetchResponse.json();
      }

      if (response && response.success) {
        message.destroy(); // 清除loading消息
        message.success(t('jobManager.taskStopped'));
        
        // 更新本地任务状态
        setJobs(prevJobs => 
          prevJobs.map(job => 
            job.id === jobId 
              ? { 
                  ...job, 
                  status: 'failed', 
                  message: '任务已手动停止',
                  progress: 0 // 重置进度
                }
              : job
          )
        );
        
        // 如果停止的是当前监控的任务，关闭连接并重置状态
        if (currentJobIdRef.current === jobId) {
          console.log('停止的是当前监控的任务，关闭SSE连接');
          closeEventSource();
          setCurrentJobId(null);
          setCurrentJobProgress(null);
          setCurrentJobStatus('idle');
          setCurrentJobEvents([]);
        }
        
        // 重新加载任务列表以获取最新状态
        await loadJobs();
        
        // 更新统计信息
        await loadJobStats();
        
        console.log(`任务 ${jobId} 已成功停止`);
      } else {
        message.destroy(); // 清除loading消息
        message.error(t('jobManager.stopTaskError'));
      }
    } catch (error: any) {
      message.destroy(); // 清除loading消息
      console.error('停止任务失败:', error);
      message.error(`${t('jobManager.stopTaskError')}: ${error.message || '未知错误'}`);
    }
  };

  /**
   * 批量停止所有运行中的任务
   */
  const stopAllRunningJobs = async () => {
    const runningJobs = jobs.filter(job => job.status === 'processing' || job.status === 'pending');
    if (runningJobs.length === 0) {
      message.info(t('jobManager.noRunningTasks'));
      return;
    }

    try {
      for (const job of runningJobs) {
        await stopJob(job.id);
      }
      message.success(t('jobManager.allTasksStopped', { count: runningJobs.length }));
      await loadJobs(); // 重新加载任务列表以更新状态
    } catch (error: any) {
      console.error('批量停止任务失败:', error);
      message.error(`${t('jobManager.stopAllError')}: ${error.message}`);
    }
  };

  // 查看任务详情
  const handleViewJobDetails = async (job: JobInfo) => {
    setSelectedJob(job);
    setJobDetailsModalVisible(true);
    setLoadingJobDetails(true);
    setSelectedJobLogs([]);
    
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const jobsPath = `${resolveSyncApiPrefix()}/jobs/${job.id}`;
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/${jobsPath}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        setSelectedJobDetails(result.data);
        
        // 优先使用 progressEvents（完整的执行日志）
        if (result.data.progressEvents && Array.isArray(result.data.progressEvents) && result.data.progressEvents.length > 0) {
          setSelectedJobLogs(result.data.progressEvents);
        } else if (result.data.steps && Array.isArray(result.data.steps) && result.data.steps.length > 0) {
          // 如果没有 progressEvents，使用 steps
          const logs = result.data.steps.map((step: any, index: number) => ({
            type: 'step',
            message: step.message || step.name || `步骤 ${index + 1}`,
            status: step.status,
            timestamp: result.data.updated_at || result.data.created_at,
            progress: Math.round((index + 1) / result.data.steps.length * 100)
          }));
          setSelectedJobLogs(logs);
        } else {
          // 如果都没有，至少显示当前任务状态
          const basicLog = {
            type: 'status',
            message: result.data.currentMessage || result.data.message || '任务执行中',
            status: result.data.status,
            timestamp: result.data.updated_at || result.data.created_at,
            progress: result.data.progress || 0
          };
          setSelectedJobLogs([basicLog]);
        }
      }
    } catch (error) {
      console.error('获取任务详情失败:', error);
      message.error(t('jobManager.getJobDetailsError'));
    } finally {
      setLoadingJobDetails(false);
    }
  };

  // 查看任务进度（在同步窗口中显示）
  const handleViewJobProgress = async (job: JobInfo) => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const monitorType: 'models' | 'instructions' | 'sql-pairs' =
        mode === 'instructions' ? 'instructions' : mode === 'sql-pairs' ? 'sql-pairs' : 'models';
      const jobsPath = `${resolveSyncApiPrefix(monitorType)}/jobs/${job.id}`;
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/${jobsPath}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        setCurrentJobId(job.id);
        setCurrentJobStatus(job.status);
        setSyncModalType(monitorType);

        const progressPayload: any = {
          jobId: job.id,
          status: result.data.status,
          progress: result.data.progress || 0,
          message: result.data.currentMessage || result.data.message || '',
          currentStep: result.data.currentStep || 0,
          totalSteps: result.data.totalSteps || 0,
          error: result.data.error,
          errors: result.data.errors,
        };

        if (monitorType === 'instructions') {
          progressPayload.isInstructionSync = true;
          progressPayload.currentInstruction = result.data.currentInstruction || '';
          progressPayload.processedInstructions = result.data.processedInstructions || 0;
          progressPayload.totalInstructions = result.data.totalInstructions || 0;
        } else if (monitorType === 'sql-pairs') {
          progressPayload.isSqlPairSync = true;
          progressPayload.currentSqlPair = result.data.currentSqlPair || '';
          progressPayload.processedSqlPairs = result.data.processedSqlPairs || 0;
          progressPayload.totalSqlPairs = result.data.totalSqlPairs || 0;
        } else {
          progressPayload.currentModel = result.data.currentModel || '';
          progressPayload.processedModels = result.data.processedModels || 0;
          progressPayload.totalModels = result.data.totalModels || 0;
        }

        setCurrentJobProgress(progressPayload);
        
        if (result.data.progressEvents && Array.isArray(result.data.progressEvents) && result.data.progressEvents.length > 0) {
          setCurrentJobEvents(result.data.progressEvents);
        } else {
          setCurrentJobEvents([]);
        }
        
        if (job.status === 'processing' || job.status === 'pending') {
          closeEventSource();
          setTimeout(() => {
            startProgressMonitoringForType(job.id, monitorType);
            message.info(t('jobManager.connectedToProgress', { jobId: job.id }));
          }, 100);
        }
        
        setSyncModalVisible(true);
      }
    } catch (error) {
      console.error('获取任务进度失败:', error);
      message.error(t('jobManager.getJobProgressError'));
    }
  };

  // 处理指令同步Excel上传
  const handleInstructionSyncUpload = async (file: File) => {
    try {
      console.log('开始处理指令文件上传:', file.name, file.size);
      setCurrentJobStatus('pending');

      if (!publishProjectId) {
        message.warning(t('modeling.importProjectRequired'));
        setCurrentJobStatus('idle');
        return;
      }
      
      // 验证文件类型
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        message.error(t('jobManager.fileTypeError'));
        setCurrentJobStatus('idle');
        return;
      }

      console.log('文件验证通过，开始调用excelUploadService');

      // 获取当前租户
      const currentTenant = authService.getCurrentTenant() || 'default';
      
      // 调用excelUploadService进行指令同步（options + X-Project-Id 由服务附加）
      const result = await excelUploadService.uploadAndSyncInstructions(file, currentTenant, undefined, publishProjectId);
      
      console.log('excelUploadService返回结果:', result);
      
      if (result.success) {
        setCurrentJobId(result.data.jobId);
        setCurrentJobStatus('processing');
        setCurrentJobEvents([]);
        
        // 开始监听进度
        console.log('开始监听进度，JobId:', result.data.jobId);
        startInstructionSyncProgressMonitoring(result.data.jobId);
        
        message.success(t('jobManager.publishStarted'));
        
        // 添加到任务列表
        const newJob: JobInfo = {
          id: result.data.jobId,
          status: 'processing',
          progress: 0,
          message: t('jobManager.info.publishInProgress'),
          timestamp: new Date().toISOString(),
          fileName: file.name,
          fileSize: file.size,
          tenantCode: currentTenant
        };
        setJobs(prev => [newJob, ...prev]);
        
      } else {
        throw new Error(result.message || t('jobManager.publishStartError'));
      }
      
    } catch (error: any) {
      console.error('指令文件上传处理失败:', error);
      message.error(`${t('jobManager.publishFailed')}: ${error.message}`);
      setCurrentJobStatus('failed');
    }
  };

  const handleSqlPairSyncUpload = async (file: File) => {
    try {
      setCurrentJobStatus('pending');
      if (!publishProjectId) {
        message.warning(t('modeling.importProjectRequired'));
        setCurrentJobStatus('idle');
        return;
      }
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        message.error(t('jobManager.fileTypeError'));
        setCurrentJobStatus('idle');
        return;
      }

      const currentTenant = authService.getCurrentTenant() || 'default';
      const result = await excelUploadService.uploadAndSyncSqlPairs(
        file,
        currentTenant,
        undefined,
        publishProjectId
      );

      if (result.success) {
        setCurrentJobId(result.data.jobId);
        setCurrentJobStatus('processing');
        setCurrentJobEvents([]);
        startSqlPairSyncProgressMonitoring(result.data.jobId);
        message.success(t('jobManager.publishStarted'));
        setJobs((prev) => [
          {
            id: result.data.jobId,
            status: 'processing',
            progress: 0,
            message: t('jobManager.info.publishInProgress'),
            timestamp: new Date().toISOString(),
            fileName: file.name,
            fileSize: file.size,
            tenantCode: currentTenant,
          },
          ...prev,
        ]);
      } else {
        throw new Error(result.message || t('jobManager.publishStartError'));
      }
    } catch (error: any) {
      console.error('SQL Pair 文件上传处理失败:', error);
      message.error(`${t('jobManager.publishFailed')}: ${error.message}`);
      setCurrentJobStatus('failed');
    }
  };

  // 文件选择处理函数
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('文件选择事件触发:', e.target.files);
    const file = e.target.files?.[0];
    if (file) {
      console.log('选择的文件:', file.name, file.size, file.type);
      if (syncModalType === 'models') {
        handleModelSyncUpload(file);
      } else if (syncModalType === 'instructions') {
        handleInstructionSyncUpload(file);
      } else if (syncModalType === 'sql-pairs') {
        handleSqlPairSyncUpload(file);
      }
    } else {
      console.log('没有选择文件');
    }
  };

  // 文件选择按钮点击事件
  const handleFileSelectClick = () => {
    console.log('按钮被点击');
    if (syncModalType === 'models' || syncModalType === 'instructions' || syncModalType === 'sql-pairs') {
      if (!publishProjectId) {
        message.warning(t('modeling.importProjectRequired'));
        return;
      }
    }
    const fileInput = document.getElementById('model-sync-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  // 获取状态标签颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'processing';
      case 'pending': return 'warning';
      case 'idle': return 'default';
      default: return 'default';
    }
  };

  // 获取状态显示文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('jobManager.status.completed');
      case 'failed': return t('jobManager.status.failed');
      case 'processing': return t('jobManager.status.processing');
      case 'pending': return t('jobManager.status.pending');
      case 'idle': return t('jobManager.status.idle');
      default: return status;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: t('jobManager.table.jobId'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: t('jobManager.table.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      render: (text: string) => text || '-',
    },
    {
      title: t('jobManager.table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: t('jobManager.table.progress'),
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number, record: JobInfo) => (
        <Progress 
          percent={progress} 
          size="small"
          status={record.status === 'failed' ? 'exception' : 'active'}
        />
      ),
    },
    {
      title: t('jobManager.table.message'),
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: t('jobManager.table.createdAt'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: string) => new Date(timestamp).toLocaleString(),
    },
    {
      title: t('jobManager.table.actions'),
      key: 'action',
      render: (record: JobInfo) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<SyncOutlined />}
            onClick={() => handleViewJobProgress(record)}
          >
            {t('jobManager.btn.viewProgress')}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewJobDetails(record)}
          >
            {t('jobManager.btn.details')}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => refreshProgress(record.id)}
            disabled={record.status === 'completed' || record.status === 'failed'}
          >
            {t('jobManager.btn.refresh')}
          </Button>
          {(record.status === 'processing' || record.status === 'pending') && (
            <Popconfirm
              title={t('jobManager.confirm.stopTask')}
              description={t('jobManager.confirm.stopTaskDesc')}
              onConfirm={() => stopJob(record.id)}
              okText={t('jobManager.confirm.ok')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true }}
              placement="topRight"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<StopOutlined />}
              >
                {t('jobManager.btn.stop')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // 组件加载时获取数据
  useEffect(() => {
    loadJobs();
    loadJobStats(); // 在组件加载时也获取统计信息
  }, []);

  // 组件卸载时清理连接
  useEffect(() => {
    return () => {
      closeEventSource();
    };
  }, []);

  return (
    <div className="job-manager-container">
      <div className="component-header">
        <Title 
          level={4} 
          className="job-manager-title"
          style={{ 
            margin: 0, 
            minWidth: 0, 
            flex: '0 1 auto',
            maxWidth: '100%',
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis' 
          }}
          title={t('jobManager.title')}
        >
          {t('jobManager.title')}
        </Title>
        <Space wrap>
          {(mode === 'models' || mode === 'both') && (
            <Button
              className="job-manager-header-btn"
              type="primary"
              icon={<SyncOutlined />}
              onClick={() => {
                setSyncModalType('models');
                // 检查是否有正在进行的任务
                const activeJob = jobs.find(job => job.status === 'processing' || job.status === 'pending');
                
                if (activeJob && (!currentJobIdRef.current || currentJobIdRef.current !== activeJob.id)) {
                  // 有活动任务但未连接，重置状态并连接
                  console.log('检测到正在进行的任务，重置状态并建立SSE连接:', activeJob.id);
                  closeEventSource();
                  setCurrentJobId(activeJob.id);
                  setCurrentJobStatus('processing');
                  setCurrentJobEvents([]);
                  setCurrentJobProgress(null);
                  
                  setTimeout(() => {
                    startModelSyncProgressMonitoring(activeJob.id);
                    message.success(t('jobManager.autoConnected', { jobId: activeJob.id }));
                  }, 100);
                } else if (activeJob && currentJobIdRef.current === activeJob.id) {
                  // 已经在监控此任务
                  console.log('已经在监控此任务:', activeJob.id);
                  message.info(t('jobManager.alreadyMonitoring'));
                } else {
                  // 没有活动任务，重置所有状态
                  console.log('没有活动任务，重置所有状态');
                  closeEventSource();
                  setCurrentJobId(null);
                  setCurrentJobStatus('idle');
                  setCurrentJobEvents([]);
                  setCurrentJobProgress(null);
                }
                
                setSyncModalVisible(true);
              }}
              style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
            >
              {t('jobManager.btn.publishModels')}
            </Button>
          )}
          {(mode === 'instructions' || mode === 'both') && (
            <Button
              className="job-manager-header-btn"
              type="primary"
              icon={<SyncOutlined />}
              onClick={() => {
                setSyncModalType('instructions');
                // 检查是否有正在进行的任务
                const activeJob = jobs.find(job => job.status === 'processing' || job.status === 'pending');
                
                if (activeJob && (!currentJobIdRef.current || currentJobIdRef.current !== activeJob.id)) {
                  // 有活动任务但未连接，重置状态并连接
                  console.log('检测到正在进行的任务，重置状态并建立SSE连接:', activeJob.id);
                  closeEventSource();
                  setCurrentJobId(activeJob.id);
                  setCurrentJobStatus('processing');
                  setCurrentJobEvents([]);
                  setCurrentJobProgress(null);
                  
                  setTimeout(() => {
                    startInstructionSyncProgressMonitoring(activeJob.id);
                    message.success(t('jobManager.autoConnected', { jobId: activeJob.id }));
                  }, 100);
                } else if (activeJob && currentJobIdRef.current === activeJob.id) {
                  // 已经在监控此任务
                  console.log('已经在监控此任务:', activeJob.id);
                  message.info(t('jobManager.alreadyMonitoring'));
                } else {
                  // 没有活动任务，重置所有状态
                  console.log('没有活动任务，重置所有状态');
                  closeEventSource();
                  setCurrentJobId(null);
                  setCurrentJobStatus('idle');
                  setCurrentJobEvents([]);
                  setCurrentJobProgress(null);
                }
                
                setSyncModalVisible(true);
              }}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              {t('jobManager.btn.publishInstructions')}
            </Button>
          )}
          {(mode === 'sql-pairs' || mode === 'both') && (
            <Button
              className="job-manager-header-btn"
              type="primary"
              icon={<SyncOutlined />}
              onClick={() => {
                setSyncModalType('sql-pairs');
                const activeJob = jobs.find((job) => job.status === 'processing' || job.status === 'pending');
                if (activeJob && (!currentJobIdRef.current || currentJobIdRef.current !== activeJob.id)) {
                  closeEventSource();
                  setCurrentJobId(activeJob.id);
                  setCurrentJobStatus('processing');
                  setCurrentJobEvents([]);
                  setCurrentJobProgress(null);
                  setTimeout(() => {
                    startSqlPairSyncProgressMonitoring(activeJob.id);
                    message.success(t('jobManager.autoConnected', { jobId: activeJob.id }));
                  }, 100);
                } else if (activeJob && currentJobIdRef.current === activeJob.id) {
                  message.info(t('jobManager.alreadyMonitoring'));
                } else {
                  closeEventSource();
                  setCurrentJobId(null);
                  setCurrentJobStatus('idle');
                  setCurrentJobEvents([]);
                  setCurrentJobProgress(null);
                }
                setSyncModalVisible(true);
              }}
              style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
            >
              {t('jobManager.btn.publishSqlPairs')}
            </Button>
          )}
          <Button
            className="job-manager-header-btn"
            icon={<ReloadOutlined />}
            onClick={loadJobs}
          >
            {t('jobManager.btn.refresh')}
          </Button>
        </Space>
      </div>

      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('jobManager.stats.total')}
              value={jobs.length}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('jobManager.stats.processing')}
              value={jobs.filter(job => job.status === 'processing' || job.status === 'pending').length}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('jobManager.stats.completed')}
              value={jobs.filter(job => job.status === 'completed').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('jobManager.stats.failed')}
              value={jobs.filter(job => job.status === 'failed').length}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 任务列表 */}
      <Card title={t('jobManager.info.taskList')} extra={
        <Space>
          <Text type="secondary">{t('jobManager.info.taskListSummary', { count: jobs.length })}</Text>
        </Space>
      }>
        {/* 批量操作按钮 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Popconfirm
              title={t('jobManager.confirm.stopAllTasks')}
              description={t('jobManager.confirm.stopAllTasksDesc')}
              onConfirm={() => stopAllRunningJobs()}
              okText={t('jobManager.confirm.okAll')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true }}
              disabled={!jobs.some(job => job.status === 'processing' || job.status === 'pending')}
            >
              <Button
                type="primary"
                danger
                icon={<StopOutlined />}
                disabled={!jobs.some(job => job.status === 'processing' || job.status === 'pending')}
              >
                {t('jobManager.btn.stopAll')}
              </Button>
            </Popconfirm>
            <Text type="secondary">
              {t('jobManager.stats.runningTasks', { count: jobs.filter(job => job.status === 'processing' || job.status === 'pending').length })}
            </Text>
          </Space>
        </div>
        
        <Table
          columns={columns}
          dataSource={jobs}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个任务`,
          }}
          scroll={{ x: true }}
        />
      </Card>

      {/* 模型同步模态框 */}
      <Modal
        title={
          syncModalType === 'models'
            ? t('jobManager.modal.publishModels')
            : syncModalType === 'sql-pairs'
            ? t('jobManager.modal.publishSqlPairs')
            : t('jobManager.modal.publishInstructions')
        }
        open={syncModalVisible}
        onCancel={() => {
          // 只隐藏模态框，不关闭SSE连接，保持进度监控活跃
          setSyncModalVisible(false);
          // 不调用 resetCurrentJob()，保持SSE连接
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            // 只隐藏模态框，不关闭SSE连接，保持进度监控活跃
            setSyncModalVisible(false);
            // 不调用 resetCurrentJob()，保持SSE连接
          }}>
            {t('common.cancel')}
          </Button>,
        ]}
        width="90%"
        style={{ maxWidth: 800 }}
      >
        <Alert
          message={
            syncModalType === 'models'
              ? t('jobManager.modal.publishModels')
              : syncModalType === 'sql-pairs'
              ? t('jobManager.modal.publishSqlPairs')
              : t('jobManager.modal.publishInstructions')
          }
          description={
            <div>
              <p>
                {syncModalType === 'models'
                  ? t('jobManager.modal.publishDescription')
                  : syncModalType === 'sql-pairs'
                  ? t('jobManager.modal.publishSqlPairsDescription')
                  : t('jobManager.modal.publishInstructionsDescription')}
              </p>
              <ul>
                <li><strong>{t('jobManager.modal.excelFormat')}</strong>：{t('jobManager.modal.excelFormatDesc')}</li>
                <li><strong>{t('jobManager.modal.publishProcess')}</strong>：{t('jobManager.modal.publishProcessDesc')}</li>
                <li><strong>{t('jobManager.modal.notice')}</strong>：{t('jobManager.modal.noticeDesc')}</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* 文件上传区域 - 显示在发布模型和指令时 */}
        {(syncModalType === 'models' || syncModalType === 'instructions' || syncModalType === 'sql-pairs') && currentJobStatus === 'idle' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            {(syncModalType === 'models' || syncModalType === 'instructions' || syncModalType === 'sql-pairs') && (
              <div style={{ marginBottom: 16, textAlign: 'left', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ marginRight: 8 }}>{t('modeling.importSelectProject')}:</Text>
                </div>
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={publishProjectOptionsLoading}
                  placeholder={t('modeling.importSelectProjectPlaceholder')}
                  value={publishProjectId || undefined}
                  style={{ width: '100%' }}
                  onChange={(v) => setPublishProjectId(v)}
                  options={publishProjectOptions}
                />
                {!publishProjectOptionsLoading && publishProjectOptions.length === 0 && (
                  <div style={{ color: '#faad14', fontSize: 12, marginTop: 4 }}>{t('modeling.importNoProjectOptions')}</div>
                )}
              </div>
            )}
            {syncModalType === 'models' && (
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <Text strong>Modeling Type:</Text>
                  <select
                    value={modelingType}
                    onChange={(e) => setModelingType(e.target.value as 'SQLSERVER' | 'HANA')}
                    style={{ padding: '4px 8px' }}
                  >
                    <option value="SQLSERVER">SQLSERVER</option>
                    <option value="HANA">HANA</option>
                  </select>
                </Space>
              </div>
            )}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="model-sync-file-input"
            />
            <Button 
              icon={<UploadOutlined />} 
              size="large"
              style={{ height: '60px', width: '200px' }}
              onClick={handleFileSelectClick}
              disabled={
                (syncModalType === 'models' || syncModalType === 'instructions' || syncModalType === 'sql-pairs') &&
                (!publishProjectId || publishProjectOptionsLoading)
              }
            >
              {t('jobManager.btn.selectFile')}
            </Button>
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">{t('jobManager.modal.fileSupport')}</Text>
            </div>
          </div>
        )}

        {/* 同步进度显示 */}
        {currentJobId && (
          <div>
            <Divider />
            <Title level={5}>{t('jobManager.info.publishProgress')}</Title>
            
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Text strong>{t('jobManager.info.taskId')}</Text>
                <Text code>{currentJobId}</Text>
                <Button 
                  size="small" 
                  onClick={() => refreshProgress(currentJobId)}
                  loading={false}
                >
                  {t('jobManager.btn.refreshProgress')}
                </Button>
                <Button 
                  size="small" 
                  onClick={() => {
                    setCurrentJobId(null);
                    resetCurrentJob();
                  }}
                >
                  {t('jobManager.btn.reset')}
                </Button>
              </Space>
            </div>
            
            {/* 连接状态 */}
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t('jobManager.info.connectionStatus')}: </Text>
              {currentJobProgress && currentJobProgress.current ? (
                <Tag color="green">{t('jobManager.info.connected')}</Tag>
              ) : (
                <Tag color="orange">{t('jobManager.info.connecting')}</Tag>
              )}
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {t('jobManager.info.lastUpdated')}: {new Date().toLocaleTimeString()}
              </Text>
            </div>
            
            {currentJobProgress && (
              <div style={{ marginBottom: 16 }}>
                {/* 总体进度条 */}
                <Progress
                  percent={currentJobProgress.progress}
                  status={currentJobProgress.status === 'completed' ? 'success' : 
                          currentJobProgress.status === 'failed' ? 'exception' : 'active'}
                  format={(percent) => `${percent}%`}
                />
                
                {/* 步骤信息（文字显示，不显示进度条） */}
                {(currentJobProgress.currentStep > 0 || currentJobProgress.totalSteps > 0) && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('jobManager.info.stepProgress', { 
                        current: currentJobProgress.currentStep || 0, 
                        total: currentJobProgress.totalSteps || 0 
                      })}
                    </Text>
                  </div>
                )}
                
                {/* 显示 Instruction 同步的详细信息 */}
                {currentJobProgress.isInstructionSync && (
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <Space size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('jobManager.info.processedInstructions', { 
                          processed: currentJobProgress.processedInstructions || 0, 
                          total: currentJobProgress.totalInstructions || 0 
                        })}
                      </Text>
                      {currentJobProgress.currentInstruction && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('jobManager.info.currentInstruction', { 
                            instruction: currentJobProgress.currentInstruction.substring(0, 50) + '...' 
                          })}
                        </Text>
                      )}
                    </Space>
                  </div>
                )}
                
                {currentJobProgress.isSqlPairSync && (
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <Space size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('jobManager.info.processedSqlPairs', {
                          processed: currentJobProgress.processedSqlPairs || 0,
                          total: currentJobProgress.totalSqlPairs || 0,
                        })}
                      </Text>
                      {currentJobProgress.currentSqlPair && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('jobManager.info.currentSqlPair', {
                            question: currentJobProgress.currentSqlPair.substring(0, 50) + '...',
                          })}
                        </Text>
                      )}
                    </Space>
                  </div>
                )}

                {/* 显示 Model 同步的详细信息 */}
                {!currentJobProgress.isInstructionSync && !currentJobProgress.isSqlPairSync && currentJobProgress.currentModel && (
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('jobManager.info.current', { model: currentJobProgress.currentModel })}
                    </Text>
                    {(currentJobProgress.processedModels !== undefined || currentJobProgress.totalModels !== undefined) && (
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        ({currentJobProgress.processedModels || 0}/{currentJobProgress.totalModels || 0})
                      </Text>
                    )}
                  </div>
                )}
                
                <div style={{ marginTop: 8 }}>
                  <Text>{currentJobProgress.message}</Text>
                </div>
                
                {/* 显示错误信息 */}
                {currentJobProgress.error && (
                  <Alert
                    message={t('jobManager.info.publishError')}
                    description={currentJobProgress.error}
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                )}
                
                {/* 显示详细错误列表 */}
                {currentJobProgress.errors && currentJobProgress.errors.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Alert
                      message={t('jobManager.info.failedCount', { count: currentJobProgress.errors.length })}
                      description={
                        <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                          {currentJobProgress.errors.map((err: any, idx: number) => (
                            <div key={idx} style={{ marginBottom: 4 }}>
                              <Tag color="red">{err.modelName}</Tag>
                              <Text type="secondary" style={{ fontSize: 12 }}>{err.error}</Text>
                            </div>
                          ))}
                        </div>
                      }
                      type="error"
                      showIcon
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* 进度事件列表 */}
            {currentJobEvents.length > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong>{t('jobManager.info.executionLog')} ({t('jobManager.info.logEvents', { count: currentJobEvents.length })})</Text>
                  <Button
                    size="small"
                    onClick={() => setCurrentJobEvents([])}
                  >
                    {t('jobManager.btn.clear')}
                  </Button>
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 8 }}>
                  {currentJobEvents.map((event, index) => {
                    // 判断是否为错误事件
                    const isError = event.error || event.message?.includes('❌') || event.message?.includes('失败');
                    
                    return (
                    <div key={index} style={{ 
                      marginBottom: 8, 
                      padding: 10,
                      backgroundColor: isError ? '#fff2f0' : '#f5f5f5',
                      borderLeft: isError ? '3px solid #ff4d4f' : '3px solid #1890ff',
                      borderRadius: 2
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Space size="small">
                          <Tag color="default">#{index + 1}</Tag>
                          <Text strong style={{ fontSize: 12, color: isError ? '#ff4d4f' : '#666' }}>
                            {event.timestamp ? new Date(event.timestamp).toLocaleString() : ''}
                          </Text>
                        </Space>
                      </div>
                      
                      {/* 消息 */}
                      <div style={{ marginBottom: 4 }}>
                        <Text 
                          type={event.message?.includes('❌') || event.message?.includes('失败') ? 'danger' : undefined}
                          style={{ 
                            whiteSpace: 'pre-wrap',
                            fontWeight: event.message?.includes('❌') || event.message?.includes('失败') ? 500 : 'normal'
                          }}
                        >
                          {event.message}
                        </Text>
                      </div>
                      
                      {/* 当前模型 */}
                      {event.currentModel && (
                        <div style={{ marginTop: 6, padding: 6, backgroundColor: '#fafafa', borderRadius: 2 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {t('jobManager.info.currentModel')}{event.currentModel}
                          </Text>
                        </div>
                      )}
                      
                      {/* 错误信息 */}
                      {event.error && (
                        <div style={{ marginTop: 6, padding: 8, backgroundColor: '#fff', border: '1px solid #ffccc7', borderRadius: 2 }}>
                          <Text type="danger" style={{ fontSize: 12, fontWeight: 500 }}>
                            ❌ {t('jobManager.info.errorInfo')}: {event.error}
                          </Text>
                        </div>
                      )}
                      
                      {/* 详细错误列表 */}
                      {event.errors && event.errors.length > 0 && (
                        <div style={{ marginTop: 6, padding: 8, backgroundColor: '#fff', border: '1px solid #ffccc7', borderRadius: 2 }}>
                          <Text type="danger" style={{ fontSize: 12, fontWeight: 500 }}>
                            {t('jobManager.info.failedModels')} ({event.errors.length}):
                          </Text>
                          <div style={{ marginTop: 4 }}>
                            {event.errors.slice(0, 3).map((err: any, idx: number) => (
                              <div key={idx} style={{ marginLeft: 8, marginTop: 2 }}>
                                <Text style={{ fontSize: 11 }}>
                                  • {err.modelName}: {err.error}
                                </Text>
                              </div>
                            ))}
                            {event.errors.length > 3 && (
                              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                                {t('jobManager.info.moreFailures', { count: event.errors.length - 3 })}
                              </Text>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 状态指示器 */}
        {currentJobStatus !== 'idle' && (
          <div style={{ marginTop: 16 }}>
            <Divider />
            <Title level={5}>{t('jobManager.table.status')}</Title>
            <Space>
              {currentJobStatus === 'uploading' && (
                <>
                  <UploadOutlined style={{ color: '#1890ff' }} />
                  <Text>{t('jobManager.info.fileUploading')}</Text>
                </>
              )}
              
              {currentJobStatus === 'syncing' && (
                <>
                  <SyncOutlined spin style={{ color: '#1890ff' }} />
                  <Text>{t('jobManager.info.publishInProgress')}</Text>
                </>
              )}
              
              {currentJobStatus === 'completed' && (
                <>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text type="success">{t('jobManager.info.publishComplete')}</Text>
                </>
              )}
              
              {currentJobStatus === 'failed' && (
                <>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <Text type="danger">{t('jobManager.info.publishFailed')}</Text>
                </>
              )}
            </Space>
          </div>
        )}
      </Modal>

      {/* 任务详情模态框 */}
      <Modal
        title={t('jobManager.modal.jobDetails')}
        open={jobDetailsModalVisible}
        onCancel={() => {
          setJobDetailsModalVisible(false);
          setSelectedJob(null);
          setSelectedJobDetails(null);
          setSelectedJobLogs([]);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setJobDetailsModalVisible(false);
            setSelectedJob(null);
            setSelectedJobDetails(null);
            setSelectedJobLogs([]);
          }}>
            {t('jobManager.btn.close')}
          </Button>,
        ]}
        width={900}
      >
        {selectedJob && (
          <div>
            <Descriptions column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('jobManager.table.jobId')} span={2}>
                <Text code>{selectedJob.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.fileName')}>
                {selectedJob.fileName || (selectedJobDetails?.request?.excel_file_path ? selectedJobDetails.request.excel_file_path.split('/').pop() : '-')}
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.tenantCode')}>
                {selectedJob.tenantCode || selectedJobDetails?.tenant_code || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.status')}>
                <Tag color={getStatusColor(selectedJob.status)}>
                  {getStatusText(selectedJob.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.progress')}>
                <Progress 
                  percent={selectedJob.progress} 
                  status={selectedJob.status === 'failed' ? 'exception' : 'active'}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.totalModels')}>
                {selectedJobDetails?.totalModels || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.processedModels')}>
                {selectedJobDetails?.processedModels || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.createdAt')}>
                {new Date(selectedJob.timestamp || selectedJobDetails?.created_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('jobManager.table.updatedAt')}>
                {selectedJobDetails?.updated_at ? new Date(selectedJobDetails.updated_at).toLocaleString() : '-'}
              </Descriptions.Item>
              {selectedJobDetails?.startTime && (
                <Descriptions.Item label={t('jobManager.table.startTime')}>
                  {new Date(selectedJobDetails.startTime).toLocaleString()}
                </Descriptions.Item>
              )}
              {selectedJobDetails?.endTime && (
                <Descriptions.Item label={t('jobManager.table.endTime')}>
                  {new Date(selectedJobDetails.endTime).toLocaleString()}
                </Descriptions.Item>
              )}
              {selectedJobDetails?.duration && (
                <Descriptions.Item label={t('jobManager.table.duration')} span={2}>
                  {Math.round(selectedJobDetails.duration / 1000)} {t('common.seconds')} ({(selectedJobDetails.duration / 1000 / 60).toFixed(2)} {t('common.minutes')})
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('jobManager.table.message')} span={2}>
                {selectedJob.message || selectedJobDetails?.currentMessage || '-'}
              </Descriptions.Item>
              {selectedJobDetails?.error && (
                <Descriptions.Item label={t('jobManager.info.errorInfo')} span={2}>
                  <Text type="danger">{selectedJobDetails.error}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* 执行日志 */}
            <Divider orientation="left">{t('jobManager.info.executionLog')}</Divider>
            {loadingJobDetails ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <SyncOutlined spin style={{ fontSize: 24, color: '#1890ff' }} />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{t('jobManager.info.loadingLogs')}</Text>
                </div>
              </div>
            ) : selectedJobLogs.length > 0 ? (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {selectedJobLogs.map((log, index) => {
                  const isError = log.error || log.message?.includes('❌') || log.message?.includes('失败') || log.status === 'failed';
                  
                  return (
                    <div 
                      key={index} 
                      style={{ 
                        marginBottom: 8, 
                        padding: 10,
                        backgroundColor: isError ? '#fff2f0' : '#f5f5f5',
                        borderLeft: isError ? '3px solid #ff4d4f' : '3px solid #1890ff',
                        borderRadius: 2
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Space size="small">
                          <Tag color="default">#{index + 1}</Tag>
                          <Text strong style={{ fontSize: 12, color: isError ? '#ff4d4f' : '#666' }}>
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                          </Text>
                        </Space>
                      </div>
                      
                      <div>
                        <Text 
                          type={isError ? 'danger' : undefined}
                          style={{ 
                            whiteSpace: 'pre-wrap',
                            fontWeight: isError ? 500 : 'normal'
                          }}
                        >
                          {log.message}
                        </Text>
                      </div>
                      
                      {/* 当前模型 */}
                      {log.currentModel && (
                        <div style={{ marginTop: 6, padding: 6, backgroundColor: '#fafafa', borderRadius: 2 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {t('jobManager.info.currentModel')}{log.currentModel}
                          </Text>
                        </div>
                      )}
                      
                      {/* 错误信息 */}
                      {log.error && (
                        <div style={{ marginTop: 6, padding: 8, backgroundColor: '#fff', border: '1px solid #ffccc7', borderRadius: 2 }}>
                          <Text type="danger" style={{ fontSize: 12, fontWeight: 500 }}>
                            ❌ {t('jobManager.info.errorInfo')}: {log.error}
                          </Text>
                        </div>
                      )}
                      
                      {/* 详细错误列表 */}
                      {log.errors && log.errors.length > 0 && (
                        <div style={{ marginTop: 6, padding: 8, backgroundColor: '#fff', border: '1px solid #ffccc7', borderRadius: 2 }}>
                          <Text type="danger" style={{ fontSize: 12, fontWeight: 500 }}>
                            {t('jobManager.info.failedModels')} ({log.errors.length}):
                          </Text>
                          <div style={{ marginTop: 4 }}>
                            {log.errors.slice(0, 3).map((err: any, idx: number) => (
                              <div key={idx} style={{ marginLeft: 8, marginTop: 2 }}>
                                <Text style={{ fontSize: 11 }}>
                                  • {err.modelName}: {err.error}
                                </Text>
                              </div>
                            ))}
                            {log.errors.length > 3 && (
                              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                                {t('jobManager.info.moreFailures', { count: log.errors.length - 3 })}
                              </Text>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Text type="secondary">{t('jobManager.info.noLogs')}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobManager;
